import { Injectable } from '@angular/core';

/**
 * A short, synthesized UI click. Using the Web Audio API (rather than an audio
 * file) keeps it asset-free, low-latency and able to overlap on rapid clicks,
 * and lets us tune/preview several presets. A small random pitch jitter is
 * applied per click so repeated taps don't sound mechanical.
 */
export type ClickSoundPreset = 'soft-tick' | 'key-pop' | 'marimba' | 'bubble' | 'click' | 'mellow';

export const CLICK_SOUND_PRESETS: ClickSoundPreset[] = [
  'soft-tick',
  'key-pop',
  'marimba',
  'bubble',
  'click',
  'mellow',
];

interface ToneSpec {
  /** 'noise' renders a filtered noise burst; otherwise an oscillator. */
  wave: OscillatorType | 'noise';
  freq: number; // Hz
  glide: number; // end-pitch ratio (1 = none, >1 up, <1 down)
  attack: number; // s
  duration: number; // s
  gain: number; // 0..1, relative to master volume
  harmonic?: number; // optional added partial (× freq) at -9 dB
  noiseQ?: number; // bandpass Q for a noise preset (lower = broader/softer)
  jitter: number; // ± fraction of pitch randomised per click
}

const PRESETS: Record<ClickSoundPreset, ToneSpec> = {
  'soft-tick': { wave: 'triangle', freq: 2000, glide: 1, attack: 0.002, duration: 0.03, gain: 0.5, jitter: 0.03 },
  'key-pop': { wave: 'sine', freq: 520, glide: 0.9, attack: 0.003, duration: 0.09, gain: 0.8, jitter: 0.02 },
  'marimba': { wave: 'sine', freq: 700, glide: 1, attack: 0.002, duration: 0.18, gain: 0.7, harmonic: 3, jitter: 0.02 },
  'bubble': { wave: 'sine', freq: 380, glide: 1.7, attack: 0.002, duration: 0.07, gain: 0.7, jitter: 0.03 },
  'click': { wave: 'noise', freq: 2200, glide: 1, attack: 0.001, duration: 0.025, gain: 0.6, noiseQ: 0.9, jitter: 0.04 },
  // Like 'click' but mellower: a lower, broader band (warmer, less harsh), a
  // gentler attack and a slightly longer tail.
  'mellow': { wave: 'noise', freq: 1000, glide: 1, attack: 0.003, duration: 0.036, gain: 0.6, noiseQ: 0.7, jitter: 0.04 },
};

const STORAGE_ENABLED = 'ap:clickSound:enabled';
const STORAGE_PRESET = 'ap:clickSound:preset';
const STORAGE_VOLUME = 'ap:clickSound:volume';
const DEFAULT_PRESET: ClickSoundPreset = 'mellow';
const DEFAULT_VOLUME = 0.6;

@Injectable({ providedIn: 'root' })
export class ClickSoundService {
  private context?: AudioContext;
  private master?: GainNode;
  private noiseBuffer?: AudioBuffer;

  enabled = this.readEnabled();
  preset = this.readPreset();
  volume = this.readVolume();

  /** Play the current click sound (no-op when disabled). */
  play(): void {
    if (!this.enabled) return;
    this.render(this.preset);
  }

  /** Play a specific preset regardless of the enabled flag (for auditioning). */
  preview(preset: ClickSoundPreset): void {
    this.render(preset);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.persist(STORAGE_ENABLED, enabled ? '1' : '0');
  }

  setPreset(preset: ClickSoundPreset): void {
    this.preset = preset;
    this.persist(STORAGE_PRESET, preset);
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    this.persist(STORAGE_VOLUME, String(this.volume));
  }

  private render(presetName: ClickSoundPreset): void {
    const ctx = this.ensureContext();
    if (!ctx || !this.master) return;
    const spec = PRESETS[presetName] ?? PRESETS[DEFAULT_PRESET];
    const now = ctx.currentTime;
    const jitter = 1 + (Math.random() * 2 - 1) * spec.jitter;

    const gain = ctx.createGain();
    const peak = Math.max(0.0001, this.volume * spec.gain);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + spec.attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + spec.duration);
    gain.connect(this.master);

    const stopAt = now + spec.duration + 0.02;
    if (spec.wave === 'noise') {
      this.playNoise(ctx, spec.freq * jitter, spec.noiseQ ?? 0.8, gain, now, stopAt, 1);
      return;
    }

    this.startOscillator(ctx, spec, spec.freq * jitter, gain, now, stopAt, 1);
    if (spec.harmonic) {
      this.startOscillator(ctx, spec, spec.freq * jitter * spec.harmonic, gain, now, stopAt, 0.35);
    }
  }

  private playNoise(
    ctx: AudioContext,
    freq: number,
    q: number,
    dest: GainNode,
    now: number,
    stopAt: number,
    level: number,
  ): void {
    const src = ctx.createBufferSource();
    src.buffer = this.ensureNoiseBuffer(ctx);
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.setValueAtTime(freq, now);
    band.Q.value = q;
    const g = ctx.createGain();
    g.gain.value = level;
    src.connect(band).connect(g).connect(dest);
    src.start(now);
    src.stop(stopAt);
  }

  private startOscillator(
    ctx: AudioContext,
    spec: ToneSpec,
    freq: number,
    dest: GainNode,
    now: number,
    stopAt: number,
    level: number,
  ): void {
    const osc = ctx.createOscillator();
    osc.type = spec.wave === 'noise' ? 'sine' : spec.wave;
    osc.frequency.setValueAtTime(freq, now);
    if (spec.glide !== 1) {
      osc.frequency.exponentialRampToValueAtTime(freq * spec.glide, now + spec.duration);
    }
    if (level === 1) {
      osc.connect(dest);
    } else {
      const g = ctx.createGain();
      g.gain.value = level;
      osc.connect(g).connect(dest);
    }
    osc.start(now);
    osc.stop(stopAt);
  }

  private ensureContext(): AudioContext | undefined {
    if (typeof window === 'undefined') return undefined;
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return undefined;
    if (!this.context) {
      try {
        this.context = new Ctor();
        this.master = this.context.createGain();
        this.master.gain.value = 1;
        this.master.connect(this.context.destination);
      } catch {
        return undefined;
      }
    }
    // Resume if the browser suspended it (allowed: we're inside a user gesture).
    if (this.context.state === 'suspended') {
      void this.context.resume();
    }
    return this.context;
  }

  private ensureNoiseBuffer(ctx: AudioContext): AudioBuffer {
    if (!this.noiseBuffer) {
      const length = Math.floor(ctx.sampleRate * 0.1);
      const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
      this.noiseBuffer = buffer;
    }
    return this.noiseBuffer;
  }

  private readEnabled(): boolean {
    const v = this.read(STORAGE_ENABLED);
    return v === null ? true : v === '1'; // default ON per the client request
  }

  private readPreset(): ClickSoundPreset {
    const v = this.read(STORAGE_PRESET);
    return v && (CLICK_SOUND_PRESETS as string[]).includes(v)
      ? (v as ClickSoundPreset)
      : DEFAULT_PRESET;
  }

  private readVolume(): number {
    const raw = this.read(STORAGE_VOLUME);
    if (raw === null) return DEFAULT_VOLUME;
    const v = Number(raw);
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : DEFAULT_VOLUME;
  }

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private persist(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* ignore storage failures */
    }
  }
}
